import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import DescriptionEditor from '@shared/components/core/field/editor/DescriptionEditor';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { SubSectionContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import { StickyFooter } from '@shared/components/core/structure/CommonBodyLayoutStyles';
import LicenseTypeLogo from '@shared/components/license/LicenseTypeLogo';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { getLicenseType } from '@shared/constants/organization/LicenseUtils';
import { useCurrentOrgDetailsEditor } from '@shared/hooks/useCurrentOrgDetailsEditor';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import { useTranslation } from 'react-i18next';
import OrgBillingComponents from '../billing/OrgBillingComponents';
import OrgNameEditor from '../details/OrgNameEditor';
import ResourceMarkdownEditor from '../details/ResourceMarkdownEditor';
import MemberDetailStack from '../member/MemberDetailStack';
import OrgManagedControlsSection from './OrgManagedControlsSection';

export interface IOrgSettingsPageContentProps {
    sendToPortal?: boolean;
}

// TODO ADMIN PORTAL: navigate with hasChanges warning
export default function OrgSettingsPageContent({ sendToPortal }: IOrgSettingsPageContentProps) {
    const { t } = useTranslation();
    const { currentOrg, membership, isLoading, canManageBilling } = useCurrentOrgDetailsFetching();
    const { handleInputChange, handleUpdateWithAlerts, editOrganization, hasChanges } = useCurrentOrgDetailsEditor(
        currentOrg ?? null
    );

    if (!currentOrg || !editOrganization || !membership) return null;
    if (isLoading) return <SpacedLoader />;
    return (
        <>
            {membership.isAdmin && (
                <>
                    <SubSectionRow title={t('common:organization.settings.general.title')} />
                    <LicenseTypeLogo licenseType={getLicenseType(currentOrg)} />
                    <SubSectionContent>
                        <OrgNameEditor
                            value={editOrganization.name ?? ''}
                            onUpdate={(field, value) => handleInputChange(field, value)}
                        />
                        <DescriptionEditor
                            value={editOrganization.description ?? ''}
                            onUpdate={(field, value) => handleInputChange(field, value)}
                        />
                    </SubSectionContent>
                    <SubSectionRow
                        title={t('common:organization.settings.resources.edit')}
                        description={t('common:organization.settings.resources.description')}
                    />
                    <ResourceMarkdownEditor
                        value={editOrganization?.customResourcesMarkdown ?? ''}
                        onUpdate={(value) => handleInputChange('customResourcesMarkdown', value ?? '')}
                    />
                </>
            )}
            <SubSectionRow title={t('common:organization.member.aboutMe')} />
            <MemberDetailStack member={membership} />

            {membership.isAdmin && (
                <>
                    <OrgManagedControlsSection organization={currentOrg} />

                    <SubSectionRow title={t('common:organization.settings.other.title')} />
                    <OrgBillingComponents currentOrg={currentOrg} canManageBilling={canManageBilling} />
                </>
            )}

            <StickyFooter $visuallyHide={!hasChanges}>
                <Button disabled={!hasChanges} onClick={async () => await handleUpdateWithAlerts()}>
                    {t('common:save')}
                </Button>
            </StickyFooter>
        </>
    );
}

const Row = styled.div`
    margin: 1rem;
`;
